import React, { useState, useContext, useEffect } from "react";

import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import Drawer from "@material-ui/core/Drawer";
import Link from "@material-ui/core/Link";
import InputLabel from "@material-ui/core/InputLabel";
import Avatar from "@material-ui/core/Avatar";
import Button from "@material-ui/core/Button";
import Paper from "@material-ui/core/Paper";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import FormControl from "@material-ui/core/FormControl";
import SwapHorizIcon from "@material-ui/icons/SwapHoriz";

import { i18n } from "../../translate/i18n";
import { toast } from "react-toastify";

import ContactModal from "../ContactModal";
import ContactDrawerSkeleton from "../ContactDrawerSkeleton";
import MarkdownWrapper from "../MarkdownWrapper";
import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";
import {
  getContactIdentifierLabel,
  hasCallablePhone
} from "../../helpers/contactIdentity";

const drawerWidth = 320;

const useStyles = makeStyles(theme => ({
	drawer: {
		width: drawerWidth,
		flexShrink: 0,
	},
	drawerPaper: {
		width: drawerWidth,
		display: "flex",
		borderTop: "1px solid rgba(0, 0, 0, 0.12)",
		borderRight: "1px solid rgba(0, 0, 0, 0.12)",
		borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
		borderTopRightRadius: 4,
		borderBottomRightRadius: 4,
	},
	header: {
		display: "flex",
		borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
		backgroundColor: "#eee",
		alignItems: "center",
		padding: theme.spacing(0, 1),
		minHeight: "73px",
		justifyContent: "flex-start",
	},
	content: {
		display: "flex",
		backgroundColor: "#eee",
		flexDirection: "column",
		padding: "8px 0px 8px 8px",
		height: "100%",
		overflowY: "scroll",
		...theme.scrollbarStyles,
	},

	contactAvatar: {
		margin: 15,
		width: 160,
		height: 160,
	},

	contactHeader: {
		display: "flex",
		padding: 8,
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center",
		"& > *": {
			margin: 4,
		},
	},

	contactDetails: {
		marginTop: 8,
		padding: 8,
		display: "flex",
		flexDirection: "column",
	},
	contactExtraInfo: {
		marginTop: 4,
		padding: 6,
	},
	alterarLinhaBtn: {
		marginTop: 8,
		borderColor: "#f57c00",
		color: "#f57c00",
		"&:hover": {
			backgroundColor: "rgba(245, 124, 0, 0.08)",
			borderColor: "#e65100",
		},
	},
}));

const ContactDrawer = ({ open, handleDrawerClose, contact, ticket, loading }) => {
	const classes = useStyles();
	const { user } = useContext(AuthContext);

	const [modalOpen, setModalOpen] = useState(false);
	const [linhaModalOpen, setLinhaModalOpen] = useState(false);
	const [whatsapps, setWhatsapps] = useState([]);
	const [selectedWhatsappId, setSelectedWhatsappId] = useState("");
	const [savingLinha, setSavingLinha] = useState(false);

	const isSuperadmin = user?.profile === "superadmin";
	const isGroupTicket = !!ticket?.isGroup;

	// Carrega lista de conexões quando abre o modal de alterar linha
	useEffect(() => {
		if (!linhaModalOpen) return;
		api.get("/whatsapp/").then(({ data }) => {
			setWhatsapps(data.filter(w => w.tipoConexao !== "cloudapi"));
			setSelectedWhatsappId(ticket?.whatsappId || "");
		}).catch(() => {
			toast.error("Erro ao carregar conexões");
		});
	}, [linhaModalOpen, ticket?.whatsappId]);

	const handleSalvarLinha = async () => {
		if (!selectedWhatsappId) return;
		setSavingLinha(true);
		try {
			await api.put(`/tickets/${ticket.id}/whatsapp`, { whatsappId: selectedWhatsappId });
			toast.success("Linha atualizada com sucesso!");
			setLinhaModalOpen(false);
		} catch (err) {
			toast.error("Erro ao atualizar linha");
		} finally {
			setSavingLinha(false);
		}
	};

	return (
		<>
			<Drawer
				className={classes.drawer}
				variant="persistent"
				anchor="right"
				open={open}
				PaperProps={{ style: { position: "absolute" } }}
				BackdropProps={{ style: { position: "absolute" } }}
				ModalProps={{
					container: document.getElementById("drawer-container"),
					style: { position: "absolute" },
				}}
				classes={{
					paper: classes.drawerPaper,
				}}
			>
				<div className={classes.header}>
					<IconButton onClick={handleDrawerClose}>
						<CloseIcon />
					</IconButton>
					<Typography style={{ justifySelf: "center" }}>
						{i18n.t("contactDrawer.header")}
					</Typography>
				</div>
				{loading ? (
					<ContactDrawerSkeleton classes={classes} />
				) : (
					<div className={classes.content}>
						<Paper square variant="outlined" className={classes.contactHeader}>
							<Avatar
								alt={contact.name}
								src={contact.profilePicUrl}
								className={classes.contactAvatar}
							></Avatar>

							<Typography>{contact.name}</Typography>
							<Typography>
								{hasCallablePhone(contact) ? (
									<Link href={`tel:${contact.phoneNumber || contact.number}`}>
										{getContactIdentifierLabel(contact)}
									</Link>
								) : (
									getContactIdentifierLabel(contact)
								)}
							</Typography>
							<Typography>
								CPF: {contact.cpf}
							</Typography>
							<Button
								variant="outlined"
								color="primary"
								onClick={() => setModalOpen(true)}
							>
								{i18n.t("contactDrawer.buttons.edit")}
							</Button>

							{/* Botão visível apenas para superadmin em tickets de grupo */}
							{isSuperadmin && isGroupTicket && (
								<Button
									variant="outlined"
									className={classes.alterarLinhaBtn}
									startIcon={<SwapHorizIcon />}
									onClick={() => setLinhaModalOpen(true)}
								>
									Alterar Linha
								</Button>
							)}
						</Paper>
						<Paper square variant="outlined" className={classes.contactDetails}>
							<ContactModal
								open={modalOpen}
								onClose={() => setModalOpen(false)}
								contact={contact}
							></ContactModal>
							<Typography variant="subtitle1">
								{i18n.t("contactDrawer.extraInfo")}
							</Typography>
							{contact?.extraInfo?.map(info => (
								<Paper
									key={info.id}
									square
									variant="outlined"
									className={classes.contactExtraInfo}
								>
									<InputLabel>{info.name}</InputLabel>
									<Typography component="div" noWrap style={{ paddingTop: 2 }}>
										<MarkdownWrapper>{info.value}</MarkdownWrapper>
									</Typography>
								</Paper>
							))}
						</Paper>
					</div>
				)}
			</Drawer>

			{/* Modal de troca de linha — superadmin + grupo */}
			<Dialog
				open={linhaModalOpen}
				onClose={() => setLinhaModalOpen(false)}
				maxWidth="xs"
				fullWidth
			>
				<DialogTitle>Alterar Linha do Grupo</DialogTitle>
				<DialogContent>
					<Typography variant="body2" style={{ marginBottom: 12 }}>
						Selecione qual conexão WhatsApp deve ser usada para enviar mensagens neste grupo.
					</Typography>
					<FormControl fullWidth variant="outlined" size="small">
						<InputLabel>Conexão</InputLabel>
						<Select
							value={selectedWhatsappId}
							onChange={e => setSelectedWhatsappId(e.target.value)}
							label="Conexão"
						>
							{whatsapps.map(w => (
								<MenuItem key={w.id} value={w.id}>
									{w.name}
									{w.id === ticket?.whatsappId ? " (atual)" : ""}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setLinhaModalOpen(false)} color="default">
						Cancelar
					</Button>
					<Button
						onClick={handleSalvarLinha}
						color="primary"
						variant="contained"
						disabled={savingLinha || !selectedWhatsappId || selectedWhatsappId === ticket?.whatsappId}
					>
						{savingLinha ? "Salvando..." : "Salvar"}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};

export default ContactDrawer;
